import React, { useState, useEffect } from 'react';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import DataTableGrid from './DataTableGrid';
import LocationsMapModal from '../modals/LocationsMapModal';
import { trackWidgetView } from '../../helpers/Analytics';

function getLocation(params) {
  return params.row.city === '(not set)' ? params.row.region : params.row.city;
}

const generateFallbackId = (row) => {
  try {
    const jsonString = JSON.stringify(row || {});
    const hash = btoa(encodeURIComponent(jsonString)).slice(0, 10).replace(/=/g, '');
    return `gen-${hash}-${Math.random().toString(36).slice(2, 8)}`;
  } catch (error) {
    return `gen-fallback-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
  }
};

const safeParseInt = (value, fallback = 0) => {
  if (typeof value === 'number') return Math.floor(value);
  const parsed = parseInt(String(value).replace(/[^0-9]/g, ''), 10);
  return Number.isSafeInteger(parsed) ? parsed : fallback;
};

const TopLocations = (props) => {
  const { setDisplayError } = useOutletContext();
  const { filtersReady } = useOutletContext();
  const [fetchingData, setFetchingData] = useState(false);
  const [rows, setRowData] = useState(null);
  const [totalRows, setTotalRows] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const handleDetailsOpen = () => setDetailsOpen(true);
  const handleDetailsClose = () => setDetailsOpen(false);

  const getData = (props, page) => {
    if (props.apiRequestOptions) {
      setRowData(null);
      setFetchingData(true);
      const safePage = safeParseInt(page, 1);

      const oParams = new URLSearchParams({
        page: safePage,
        pageSize: 5,
        sort: 'page_views',
        hostname: props.domains,
        startDate: props.startDate.toISODate(),
        endDate: props.endDate.toISODate(),
      });

      const oAxiosOptions = {
        headers: props.apiRequestOptions.headers,
        params: oParams,
      };

      axios.get('/api/v1/site/page_visitor_location', oAxiosOptions).then(
        (response) => {
          try {
            const resData = response.data || {};
            const rawData = Array.isArray(resData.data) ? resData.data : [];
            const pageNumber = safeParseInt(resData.page, safePage);
            const total = safeParseInt(resData.totalRows, 0);

            const newRows = rawData.map((row, index) => ({
              ...row,
              id: row?.id || `loc-${(pageNumber - 1) * 5 + index}-${generateFallbackId(row)}`,
            }));

            setRowData(newRows);
            setTotalRows(total);
            setCurrentPage(pageNumber);
          } catch (error) {
            setDisplayError(new Error('Data processing failed'));
            setRowData([]);
          }
          setFetchingData(false);
          trackWidgetView({ name: 'Top Locations' });
        },
        (error) => {
          setDisplayError(error);
          setRowData([]);
          setFetchingData(false);
        }
      );
    }
  };

  const handlePageChange = (newPage) => {
    if (filtersReady && !fetchingData && props.domains) {
      getData(props, newPage);
    }
  };

  useEffect(() => {
    if (filtersReady && !fetchingData && props.domains) {
      getData(props);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.domains, props.startDate, props.endDate]);

  useEffect(() => {
    props.setLoadingTopLocations(fetchingData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchingData]);

  const options = {
    pageSize: 5,
    currentPage: currentPage,
    rowCount: totalRows,
    handlePageChange: handlePageChange,
    sortBy: 'pageviews',
    approxHeight: 300,
    hideFooter: true,
  };

  const columns = [
    {
      field: 'id',
      maxWidth: 30,
      minWidth: 30,
      headerName: '',
      sortable: false,
      renderCell: (params) => params.row.id?.toString() || '–',
    },
    {
      field: 'city',
      headerName: 'Location',
      sortable: false,
      flex: 1,
      valueGetter: getLocation,
    },
    {
      field: 'page_views',
      headerName: 'Pageviews',
      sortable: false,
      flex: 1,
      headerAlign: 'right',
      type: 'number',
    },
  ];

  return (
    <>
      <Typography variant="h2" gutterBottom sx={{ mr: 1 }}>
        Top Locations
      </Typography>
      {rows !== null ? (
        <>
          <DataTableGrid columns={columns} rows={rows} options={options} />
          <Box sx={{ textAlign: 'center', my: 1 }}>
            <Button onClick={handleDetailsOpen} size="small" variant="outlined" color="neutral">
              View Map
            </Button>
          </Box>
        </>
      ) : (
        <Skeleton variant="rectangular" height={300} />
      )}
      <LocationsMapModal
        detailsOpen={detailsOpen}
        handleDetailsClose={handleDetailsClose}
        domains={props.domains}
        startDate={props.startDate}
        endDate={props.endDate}
        dateRangeLabel={props.dateRangeLabel}
        apiRequestOptions={props.apiRequestOptions}
      />
    </>
  );
};

export default TopLocations;
